import type { UploadPolicy } from '@/types/project-reference';
import type {
  ProcessedFile,
  UploadResult,
} from '@/shared/lib/services/image-upload.service';
import type { UploadType } from '@/shared/constants';

// ===== Item / Batch =====

export interface UploadItem {
  id: string;
  file: File;
  type: UploadType;
  status:
    | 'pending'
    | 'processing'
    | 'uploading'
    | 'completed'
    | 'failed'
    | 'cancelled';
  progress: number;
  /**
   * What was thrown, verbatim — never prose. Wording an upload failure inside
   * the store meant an English string (an S3 XML body among them) reaching a
   * card in all 24 locales; the render site localizes it instead, through the
   * same `localizeUploadError` the toasts already use.
   */
  error?: unknown;
  result?: UploadResult;
  retryCount: number;
  startedAt?: number;
  completedAt?: number;
  processedFile?: ProcessedFile;
  uploadPolicy?: UploadPolicy;
}

export interface UploadBatch {
  id: string;
  items: UploadItem[];
  mainAngleIndex?: number;
  totalProgress: number;
  status:
    | 'pending'
    | 'processing'
    | 'uploading'
    | 'completed'
    | 'failed'
    | 'cancelled';
  createdAt: number;
  completedAt?: number;
}

// ===== Store data shape =====

export interface UploadStats {
  totalUploaded: number;
  totalFailed: number;
  totalBytes: number;
  averageSpeed: number;
  lastUploadTime?: number;
}

export interface UploadUiState {
  loading: { upload: boolean };
  showProgressBar: boolean;
  currentBatchId: string | null;
}

export interface UploadData {
  // Core data
  queue: UploadItem[];
  batches: Record<string, UploadBatch>;
  activeUploads: string[];
  completedUploads: UploadItem[];
  failedUploads: UploadItem[];

  stats: UploadStats;
  ui: UploadUiState;
}

export const initialUploadData: UploadData = {
  queue: [],
  batches: {},
  activeUploads: [],
  completedUploads: [],
  failedUploads: [],
  stats: {
    totalUploaded: 0,
    totalFailed: 0,
    totalBytes: 0,
    averageSpeed: 0,
  },
  ui: {
    loading: { upload: false },
    showProgressBar: false,
    currentBatchId: null,
  },
};

// ===== ID generators =====
//
// Inlined into createBatch / addToQueue / etc previously — extracted to keep
// the random-suffix recipe in one place and surface accidental collisions if
// callers ever start sharing the prefix.

export function generateItemId(): string {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function generateBatchId(): string {
  return `batch-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
